import type { Block } from '../types';
import type { OpenCommitment, OpenCommitmentOption } from '../types';

const PROCEED_ASK_RE =
  /\s*((?:Should I|Would you like(?: me)? to) proceed(?: with (?:this|the) plan)?\??)\s*$/i;
const BULLET_SPLIT_RE = /\s*[•·]\s*/;
const BULLET_PREFIX_RE = /^[-*•·]\s*(.+)$/;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function optionLabels(commitment: OpenCommitment): string[] {
  return (commitment.options ?? []).map(option => option.label.trim()).filter(Boolean);
}

function questionKeys(question: string): Set<string> {
  const base = question.replace(/\?$/, '').trim().toLowerCase();
  if (!base) {
    return new Set();
  }
  return new Set([base, `${base}?`]);
}

function isQuestionText(text: string, question: string): boolean {
  if (!question) {
    return false;
  }
  return questionKeys(question).has(text.trim().toLowerCase());
}

function isOptionText(text: string, labelSet: Set<string>): boolean {
  const stripped = text.trim();
  if (!stripped) {
    return false;
  }
  const match = stripped.match(BULLET_PREFIX_RE);
  if (match) {
    return labelSet.has(match[1].trim().toLowerCase());
  }
  return labelSet.has(stripped.toLowerCase());
}

function stripInlineOptionBullets(text: string, question: string, labelSet: Set<string>): string {
  const segments = text
    .split(BULLET_SPLIT_RE)
    .map(segment => segment.trim())
    .filter(Boolean);
  if (segments.length === 0) {
    return text.trim();
  }

  const kept: string[] = [];
  for (const segment of segments) {
    if (isQuestionText(segment, question)) {
      continue;
    }
    if (isOptionText(segment, labelSet)) {
      continue;
    }
    if (question) {
      const qBase = question.replace(/\?$/, '').trim();
      if (segment.toLowerCase().startsWith(qBase.toLowerCase())) {
        const remainder = segment.slice(qBase.length).replace(/^\?/, '').trim();
        if (!remainder || isOptionText(remainder, labelSet)) {
          continue;
        }
        kept.push(remainder);
        continue;
      }
    }
    kept.push(segment);
  }
  return kept.join(' ').trim();
}

function stripMultilineOptionBullets(text: string, question: string, labelSet: Set<string>): string {
  const kept: string[] = [];
  for (const line of text.split('\n')) {
    const stripped = line.trim();
    if (!stripped || isQuestionText(stripped, question) || isOptionText(stripped, labelSet)) {
      continue;
    }
    kept.push(stripped);
  }
  return kept.join('\n').trim();
}

/**
 * Remove the owned ask from agent prose when the OpenCommitmentCard will render it.
 * Mirrors DESO `strip_open_commitment_question_from_message` for cached/history turns.
 */
export function stripOpenCommitmentQuestionFromProse(
  prose: string,
  commitment: OpenCommitment,
): string {
  let text = (prose ?? '').trim();
  if (!text) {
    return text;
  }

  const question = (commitment.question ?? '').trim();
  const labelSet = new Set(optionLabels(commitment).map(label => label.toLowerCase()));
  const hasOptions = (commitment.options?.length ?? 0) > 0;

  if (hasOptions) {
    if (text.includes('•') || text.includes('·')) {
      text = stripInlineOptionBullets(text, question, labelSet);
    }
    if (text.includes('\n')) {
      text = stripMultilineOptionBullets(text, question, labelSet);
    }
    if (question) {
      const patterns = [
        new RegExp(`\\s*${escapeRegExp(question)}\\s*$`, 'i'),
        new RegExp(`\\s*${escapeRegExp(question.replace(/\?$/, '').trim())}\\??\\s*$`, 'i'),
      ];
      for (const pattern of patterns) {
        text = text.replace(pattern, '').trimEnd();
      }
      if (isQuestionText(text, question)) {
        text = '';
      }
    }
  } else if (question) {
    const patterns = [
      new RegExp(`\\s*${escapeRegExp(question)}\\s*$`, 'i'),
      new RegExp(`\\s*${escapeRegExp(question.replace(/\?$/, '').trim())}\\??\\s*$`, 'i'),
    ];
    for (const pattern of patterns) {
      text = text.replace(pattern, '').trimEnd();
    }
    if (text.toLowerCase() === question.toLowerCase()) {
      text = '';
    }
  }

  if (commitment.kind.endsWith('_confirm')) {
    text = text.replace(PROCEED_ASK_RE, '').trimEnd();
  }

  return text.trim();
}

/** Drop parallel follow_up_chips when open_commitment already owns the options. */
export function filterBlocksForOpenCommitment(
  blocks: Block[] | null,
  commitment: OpenCommitment | null,
): Block[] | null {
  if (!blocks || !commitment?.options?.length) {
    return blocks;
  }
  const filtered = blocks.filter(block => block.type !== 'follow_up_chips');
  return filtered.length > 0 ? filtered : null;
}

/** True when prose still contains the owned question after stripping. */
export function proseStillContainsOpenCommitmentQuestion(
  prose: string,
  commitment: OpenCommitment,
): boolean {
  const question = (commitment.question ?? '').trim();
  if (!question) {
    return false;
  }
  return prose.trim().toLowerCase().includes(question.toLowerCase());
}

/** @internal test helper */
export function _optionLabelsForTest(options: OpenCommitmentOption[]): string[] {
  return options.map(option => option.label);
}
